import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";

import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";

import { RootState, useAppDispatch } from "../../../redux/store";
import {
    VariazioneDisponibilita,
    saveVariazione,
    removeVariazione,
} from "./variazioniDisponibilitaSlice";
import { Reparto } from "../../anagrafiche/reparti/repartiSlice";
import { Operatore } from "../personale/operatoriSlice";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

// ============================================
// PROPS
// ============================================

interface VariazioneEditModalProps {
    open: boolean;
    variazione: VariazioneDisponibilita | undefined;   // undefined → niente da mostrare (chiuso)
    onClose: () => void;
    onSaved?: (saved: VariazioneDisponibilita) => void;
}

// ============================================
// FORM SHAPE
// ============================================

interface VariazioneFormShape {
    reparto: number | null;
    operatore: number | null;
    data_da: string;
    data_a: string;
    ore_giornaliere: number;
    note: string;
}

const toIso = (dt: DateTime | null): string => (dt && dt.isValid ? dt.toISODate()! : "");

// ============================================
// COMPONENT
// ============================================

const VariazioneEditModal = ({ open, variazione, onClose, onSaved }: VariazioneEditModalProps) => {
    const dispatch = useAppDispatch();

    const reparti = useSelector<RootState, Reparto[]>(
        (state) => state.repartiState?.repartiList?.results || []
    );
    const operatori = useSelector<RootState, Operatore[]>(
        (state) => state.operatoriState?.operatoriList?.results || []
    );

    const { control, handleSubmit, reset } = useForm<VariazioneFormShape>({
        defaultValues: {
            reparto: variazione?.reparto ?? null,
            operatore: variazione?.operatore ?? null,
            data_da: variazione?.data_da ?? "",
            data_a: variazione?.data_a ?? "",
            ore_giornaliere: variazione?.ore_giornaliere ?? 1,
            note: variazione?.note ?? "",
        },
    });

    useEffect(() => {
        if (variazione) {
            reset({
                reparto: variazione.reparto ?? null,
                operatore: variazione.operatore ?? null,
                data_da: variazione.data_da ?? "",
                data_a: variazione.data_a ?? "",
                ore_giornaliere: variazione.ore_giornaliere ?? 1,
                note: variazione.note ?? "",
            });
        }
    }, [variazione, reset]);

    // Operatori disponibili: TUTTI gli attivi. Il pianificatore può scegliere
    // anche operatori di altri reparti (spostamento inter-reparto) o senza
    // reparto-base (assegnamento temporaneo). I vincoli semantici sono applicati
    // dal backend in clean().
    const operatoriDisponibili = useMemo(
        () => operatori.filter((op) => op.attivo),
        [operatori],
    );

    const formatErroreBackend = (errore: any): string => {
        // I 400 di DRF arrivano come stringa JSON nel .message dell'Error.
        if (!errore) return "errore sconosciuto";
        try {
            const obj = typeof errore.message === "string" ? JSON.parse(errore.message) : errore.message;
            if (typeof obj === "string") return obj;
            const parts: string[] = [];
            Object.keys(obj).forEach((k) => {
                const v = obj[k];
                if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
                else parts.push(`${k}: ${v}`);
            });
            return parts.join(" — ") || "errore di validazione";
        } catch {
            return String(errore.message || errore);
        }
    };

    const onSubmit = async (data: VariazioneFormShape) => {
        if (!data.reparto) return;
        const payload: VariazioneDisponibilita = {
            ...(variazione?.id ? { id: variazione.id } : {}),
            reparto: data.reparto,
            operatore: data.operatore ?? null,
            data_da: data.data_da,
            data_a: data.data_a,
            ore_giornaliere: data.ore_giornaliere,
            note: data.note || null,
        };
        const result = await dispatch(saveVariazione(payload));
        if (saveVariazione.fulfilled.match(result)) {
            onSaved?.(result.payload as VariazioneDisponibilita);
            onClose();
        } else {
            toast.error("Salvataggio fallito: " + formatErroreBackend(result.error));
        }
    };

    const onDelete = async () => {
        if (!variazione?.id) return;
        const result = await dispatch(removeVariazione(variazione.id));
        if (removeVariazione.fulfilled.match(result)) {
            onClose();
        } else {
            toast.error("Eliminazione fallita: " + formatErroreBackend(result.error));
        }
    };

    const isEdit = !!variazione?.id;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEdit ? "Modifica placeholder di disponibilità" : "Nuovo placeholder di disponibilità"}
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Le ore positive aggiungono disponibilità al reparto/operatore; le negative la sottraggono.
                            Per spostare un operatore su un altro reparto: scegli reparto destinazione + operatore (di altro reparto-base) + ore positive.
                        </Typography>

                        <Controller
                            name="reparto"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Reparto destinazione *"
                                    fullWidth
                                    size="small"
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                        const id = e.target.value === "" ? null : Number(e.target.value);
                                        field.onChange(id);
                                    }}
                                >
                                    {reparti
                                        .filter((r) => r.attivo)
                                        .map((r) => (
                                            <MenuItem key={r.id} value={r.id}>
                                                {r.codice} - {r.descrizione}
                                            </MenuItem>
                                        ))}
                                </TextField>
                            )}
                        />

                        <Controller
                            name="operatore"
                            control={control}
                            render={({ field }) => {
                                const value = operatoriDisponibili.find((op) => op.id === field.value) || null;
                                return (
                                    <Autocomplete
                                        options={operatoriDisponibili}
                                        value={value}
                                        getOptionLabel={(op) => `${op.codice} - ${op.nominativo || ""}`}
                                        onChange={(_, newValue) => field.onChange(newValue?.id ?? null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Operatore (lascia vuoto per reparto-puro)"
                                            />
                                        )}
                                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                                    />
                                );
                            }}
                        />

                        <Stack direction="row" spacing={2}>
                            <Controller
                                name="data_da"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <DesktopDatePicker
                                        label="Data inizio *"
                                        format="dd/MM/yyyy"
                                        value={field.value ? DateTime.fromISO(field.value) : null}
                                        onChange={(dt) => field.onChange(toIso(dt))}
                                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    />
                                )}
                            />
                            <Controller
                                name="data_a"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <DesktopDatePicker
                                        label="Data fine *"
                                        format="dd/MM/yyyy"
                                        value={field.value ? DateTime.fromISO(field.value) : null}
                                        onChange={(dt) => field.onChange(toIso(dt))}
                                        slotProps={{ textField: { size: "small", fullWidth: true } }}
                                    />
                                )}
                            />
                        </Stack>

                        <Controller
                            name="ore_giornaliere"
                            control={control}
                            rules={{ required: true, validate: (v) => v !== 0 || "Le ore non possono essere zero" }}
                            render={({ field, fieldState }) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Ore al giorno (può essere negativo) *"
                                    size="small"
                                    fullWidth
                                    decimalSeparator=","
                                    decimalScale={2}
                                    allowNegative
                                    suffix=" h"
                                    value={field.value}
                                    onValueChange={(values) =>
                                        field.onChange(values.floatValue ?? 0)
                                    }
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />

                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Note"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    size="small"
                                />
                            )}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {isEdit && (
                        <Button color="error" onClick={onDelete} sx={{ mr: "auto" }}>
                            Elimina
                        </Button>
                    )}
                    <Button onClick={onClose}>Annulla</Button>
                    <Button type="submit" variant="contained">
                        Salva
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default VariazioneEditModal;
